import { useEffect, useState } from "react";
import { toThreadMessage } from "@repo/ui/chat";
import type { KyInstance, SessionMessagesResponse, ThreadMessageLike } from "@repo/ui/chat";

interface SessionMessagesState {
  messages: ThreadMessageLike[];
  loading: boolean;
  error: string | undefined;
}

const NO_SESSION: SessionMessagesState = { messages: [], loading: false, error: undefined };

export function useSessionMessages(
  api: KyInstance,
  sessionId: string | undefined,
): SessionMessagesState {
  const [messages, setMessages] = useState<ThreadMessageLike[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      setLoading(false);
      setError(undefined);
      return;
    }

    const id = sessionId;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(undefined);
      try {
        const body = await api
          .get(`sessions/${encodeURIComponent(id)}/messages`)
          .json<SessionMessagesResponse>();
        if (!cancelled) setMessages((body.messages ?? []).map(toThreadMessage));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load messages");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [api, sessionId]);

  if (!sessionId) return NO_SESSION;
  return { messages, loading, error };
}
